<template>
  <v-container fluid>
    <page-header showBackButton>
      <template slot="botRightSlot" v-if="id !== null">
        <v-btn
          depressed
          class="ml-2 mb-4"
          elevation="0"
          color="primary"
          :disabled="$store.getters.isLoading"
          :to="{ name: 'UserDetail', params: { id: formData.user_id } }"
          >{{ $t('viewAccount') }}</v-btn
        >

        <v-btn
          v-if="staticData.payment_status === 'paid' && staticData.receipt"
          depressed
          class="ml-2 mb-4"
          elevation="0"
          color="primary"
          :disabled="$store.getters.isLoading"
          target="_blank"
          :download="receiptName"
          :href="`${$mediaPath}${staticData.receipt}`"
        >{{ $t('downloadReceipt') }}</v-btn>
      </template>
    </page-header>

    <v-card class="mb-4">
      <v-card-text>
        <v-form ref="basicForm" @submit.prevent v-if="id === null">
          <v-row class="ma-0">
            <v-col cols="12" lg="6">
              <form-search-select
                label="company"
                :fieldValue.sync="formData.company_id"
                required
                :options="companyOptions"
                @input-search="searchCompany($event)"
                :searching="companySearching"
                searchPlaceholder="companySearchPlaceholder"
                @onselect="handleCompanySelected($event)"
              />
            </v-col>
          </v-row>
        </v-form>

        <v-row class="ma-0" v-if="selectedCompanyData">
          <v-col cols="12" sm="6" lg="3" v-if="id !== null">
            <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('orderDateTime') }}</label>
            <div class="colorBlack--text">{{ $Formatter.displayCreateDate(formData.create_date) }}</div>
          </v-col>
          <v-col cols="12" sm="6" lg="3" v-if="id !== null">
            <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('orderId') }}</label>
            <div class="colorBlack--text">
              {{ $Formatter.getOrderId({ id: id, create_date: formData.create_date }) }}
            </div>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('companyType') }}</label>
            <div class="colorBlack--text" v-if="selectedCompanyData.type">
              {{ $t('companyTypeOption.' + selectedCompanyData.type) }}
            </div>
            <div class="colorBlack--text" v-else>-</div>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('companyNameEn') }}</label>
            <div class="colorBlack--text">{{ selectedCompanyData.name_en }}</div>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('companyNameZh') }}</label>
            <div class="colorBlack--text">{{ selectedCompanyData.name_zh }}</div>
          </v-col>
          <template v-if="id !== null">
            <v-col cols="12" sm="6" lg="3">
              <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('paymentMethod') }}</label>
              <div class="colorBlack--text">
                {{ formData.payment_method ? $t(`paymentMethodOptions.${formData.payment_method}`) : '-' }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" lg="3" v-if="formData.payment_method === 'transfer'">
              <v-btn
                x-large
                elevation="0"
                color="primary"
                :disabled="!formData.payment_file"
                target="_blank"
                :href="`${$mediaPath}${formData.payment_file}`"
                >{{ $t('viewPaymentFile') }}</v-btn
              >
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <v-form ref="paymentStatusForm" @submit.prevent>
                <form-select
                  label="status"
                  :fieldValue.sync="formData.payment_status"
                  :options="paymentStatusOptions"
                  required
                />
              </v-form>
            </v-col>
          </template>
        </v-row>

        <v-btn
          v-if="id !== null"
          depressed
          elevation="0"
          class="ml-2 mb-4"
          color="primary"
          type="button"
          @click="save()"
          :loading="$store.getters.isLoading"
          >{{ $t('save') }}</v-btn
        >
      </v-card-text>
    </v-card>

    <v-card class="mb-8" v-if="selectedCompanyData">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12" class="d-flex align-center justify-space-between">
            <h4 class="section-title">{{ $t('service') }}</h4>
            <v-btn
              v-if="id === null"
              @click="openAddServiceForm()"
              depressed
              elevation="0"
              class="ml-2 mb-4"
              color="primary"
              type="button"
              >{{ $t('add') }}</v-btn
            >
          </v-col>
          <v-col cols="12">
            <datatable
              :tableHeaders="tableHeaders"
              :tableData="formData.plans"
              disableToolbar
              disablePagination
              disableShadow
              @delete-clicked="deleteService($event)"
              @view-clicked="openAddServiceForm('edit', $event)"
            />
            <div class="d-flex mt-2 text-h6 justify-end" v-if="id === null">
              {{ $t('totalPrice', { total: totalPrice }) }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-btn
      v-if="id === null"
      depressed
      elevation="0"
      class="ml-2 mb-4"
      color="primary"
      type="button"
      @click="addBill()"
      :loading="$store.getters.isLoading"
      >{{ $t('save') }}</v-btn
    >

    <!-- Modal: 新增服務Form -->
    <form-dialog
      ref="addServiceDialog"
      formRef="addServiceForm"
      title="addService"
      :maxWidth="1200"
      @submit-clicked="submitAddService()"
      v-if="id === null"
    >
      <v-row class="ma-0">
        <v-col cols="12" sm="6" lg="4">
          <form-select
            label="selectService"
            :fieldValue.sync="addServiceData.type"
            :options="serviceOptions"
            required
            @changed="onAddServiceTypeChanged()"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" v-if="addServiceOptions.length">
          <form-select
            label="selectPlan"
            :fieldValue.sync="addServiceData.plan"
            :options="addServiceOptions"
            required
            @changed="onAddServicePlanChanged()"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4">
          <form-input label="price" :fieldValue.sync="addServiceData.price" type="number" prefix="HK$" required />
        </v-col>
      </v-row>
    </form-dialog>
  </v-container>
</template>

<script>
import FormSelect from '@/components/form/FormSelect.vue'
import Datatable from '@/components/Datatable.vue'
import FormSearchSelect from '@/components/form/FormSearchSelect.vue'
import FormDialog from '@/components/FormDialog.vue'
import FormInput from '@/components/form/FormInput.vue'
export default {
  name: 'InvoiceDetail',
  components: {
    FormSelect,
    Datatable,
    FormSearchSelect,
    FormDialog,
    FormInput,
  },
  computed: {
    id() {
      const id = parseInt(this.$route.params.id)
      if (!isNaN(id)) {
        return id
      }

      return null
    },
    serviceOptions() {
      let list = []
      if (this.selectedCompanyData) {
        if (this.selectedCompanyData.type === 'limited') {
          list.push({ text: this.$t('planTypeOption.create_company_limited'), value: 'create_company_limited' })
        } else if (this.selectedCompanyData.type === 'unlimit') {
          list.push({ text: this.$t('planTypeOption.create_company_unlimited'), value: 'create_company_unlimited' })
        }
        if (this.selectedCompanyData.type !== 'unlimit') {
          list.push({ text: this.$t('planTypeOption.secretary'), value: 'secretary' })
        }

        list = [
          ...list,
          { text: this.$t('planTypeOption.virtual_office'), value: 'virtual_office' },
          { text: this.$t('planTypeOption.update_corporate_info'), value: 'update_corporate_info' },
          { text: this.$t('planTypeOption.deregistration'), value: 'deregistration' },
          { text: this.$t('planTypeOption.br'), value: 'br' },
          { text: this.$t('planTypeOption.scr'), value: 'scr' },
          { text: this.$t('planTypeOption.stamp'), value: 'stamp' },
        ]
      }

      return list
    },
    totalPrice() {
      return this.formData.plans.reduce((value, item) => value + parseFloat(item.price), 0)
    },
    tableHeaders() {
      const headers = [
        { value: 'service', text: this.$t('service') },
        { value: 'price', text: this.$t('price'), prefix: 'HK$' },
      ]

      if (this.id === null) {
        headers.push({ value: 'actionViewDelete', text: '', align: 'end' })
      }
      return headers
    },
    receiptName() {
      if (this.staticData.receipt) {
        let name = this.staticData.receipt.split('.')[0];
        name = name.split('_').pop();
        return name;
      }
      return '';
    },
  },
  data: vm => ({
    staticData: {
      payment_status: '',
      receipt: '',
    },
    formData: {
      create_date: '',
      payment_method: '',
      payment_file: '',
      payment_status: '',
      user_id: -1,
      company_id: -1,
      plans: [],
    },
    billPlans: [],

    companySearching: false,
    companyOptions: [],
    selectedCompanyData: null,

    allPlanData: [],
    addServiceOptions: [],
    addServiceData: {
      type: '',
      plan: -1,
      price: '',
    },
    addServiceType: '',
    paymentStatusOptions: [
      { value: 'paid', text: vm.$t('paymentStatusOptions.paid') },
      { value: 'pending', text: vm.$t('paymentStatusOptions.pending') },
      { value: 'unpaid', text: vm.$t('paymentStatusOptions.unpaid') },
    ],
  }),
  methods: {
    // ----------------- 新增訂單 -----------------
    async searchCompany(keyword = '') {
      if (this.companySearching) {
        return
      }

      this.companySearching = true
      let payload = {}
      if (keyword) {
        payload['search'] = keyword
      }

      try {
        const { data } = await this.$Fetcher.GetCompanies(payload)
        this.companyOptions = data.map(item => {
          return {
            value: item.id,
            text: item.name_zh || item.name_en,
            ...item,
          }
        })
      } catch (err) {
        this.$common.error(err)
        this.companyOptions = []
      } finally {
        this.companySearching = false
      }
    },
    handleCompanySelected(value) {
      this.selectedCompanyData = this.companyOptions.find(el => el.value === value)
    },
    async getAllPlans() {
      try {
        const { data } = await this.$Fetcher.GetPlans({
          filters: [{ key: 'status', value: 'active' }],
        })
        this.allPlanData = data
      } catch (err) {
        this.$common.error(err)
      }
    },

    async addBill() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const valid = await this.$refs['basicForm'].validate()
      if (!valid || this.formData.plans.length < 1 || !this.selectedCompanyData) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        return
      }

      this.$store.dispatch('setLoading', true)

      const payload = {
        amount: this.totalPrice,
        company_id: this.selectedCompanyData.id,
        user_id: this.selectedCompanyData.user_id,
        plan_id_list: [],
      }

      payload['plan_id_list'] = this.formData.plans.map(item => {
        return {
          id: item.id,
          price: parseFloat(item.price),
        }
      })

      try {
        const data = await this.$Fetcher.NewBill(payload)
        this.$store.dispatch('setDataIsUpdated', false)
        await this.sendAlert(data.id)
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setAlert', {
          title: 'message.saveFail',
          message: err,
          type: 'error',
        })
      }
    },
    async sendAlert(id) {
      try {
        await this.$Fetcher.NewAlert({
          type: 'bill',
          data: 'bill_data',
          data_id: id,
          company_id: this.selectedCompanyData.id,
          user_id: this.selectedCompanyData.user_id,
        })
      } catch (err) {
        this.$common.error(err)
      } finally {
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('toggleSnack', {
          type: 'success',
          message: 'message.saveSuccess',
          redirect: 'OrderList',
        })
      }
    },
    // ------ 新增服務 ------
    async openAddServiceForm(type = 'new', id) {
      if (this.$store.getters.isLoading) {
        return
      }

      this.addServiceType = type
      this.addServiceData.type = ''
      this.addServiceData.plan = -1
      this.addServiceData.price = ''
      this.addServiceOptions = []
      if (type === 'edit') {
        this.$refs['addServiceDialog'].setEditId(id)
        const found = this.formData.plans.find(el => el.id === id)
        this.addServiceData.type = found.type
        this.addServiceData.plan = found.id
        this.addServiceData.price = found.price
        this._getServiceOptions()
      }

      this.$refs['addServiceDialog'].show()
    },

    onAddServiceTypeChanged() {
      this.addServiceData.plan = -1
      this.addServiceData.price = ''
      this._getServiceOptions()

      let plans = this.allPlanData.filter(el => el.type === this.addServiceData.type)
      if (this.addServiceOptions.length < 1) {
        this.addServiceData.plan = plans.length ? plans[0].id : -1
        this.addServiceData.price = plans[0]?.price || ''
      }
    },
    _getServiceOptions() {
      this.addServiceOptions = []
      let plans = this.allPlanData.filter(el => el.type === this.addServiceData.type)
      if (this.addServiceData.type === 'update_corporate_info') {
        if (this.selectedCompanyData.type !== '' && this.selectedCompanyData.type !== 'group_assoc') {
          plans = plans.filter(el => el.company_type === '' || el.company_type === this.selectedCompanyData.type)
        }
      }
      if (plans.length > 1) {
        this.addServiceOptions = plans.map(el => {
          return { value: el.id, text: `${el.name_zh} ${el.name_en}` }
        })
      }
    },

    onAddServicePlanChanged() {
      this.addServiceData.price = ''
      if (this.addServiceData.plan > -1) {
        const found = this.allPlanData.find(el => el.id === this.addServiceData.plan)
        if (found) {
          this.addServiceData.price = found.price || ''
        }
      }
    },

    submitAddService() {
      if (this.addServiceData.plan < -1) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        this.$refs['addServiceDialog'].setLoading(false)
        return
      }

      const foundPlan = this.allPlanData.find(el => el.id === this.addServiceData.plan)
      const obj = {
        id: foundPlan.id,
        service: this.$Formatter.getServiceName(foundPlan.name_zh, this.$t(`planTypeOption.${foundPlan.type}`)),
        price: this.addServiceData.price,
        type: this.addServiceData.type,
      }
      if (this.addServiceType === 'edit') {
        const id = this.$refs['addServiceDialog'].getEditId()
        const foundPos = this.formData.plans.findIndex(el => el.id === id)
        this.$set(this.formData.plans, foundPos, obj)
      } else {
        // check repeat
        const found = this.formData.plans.find(el => el.id === this.addServiceData.plan)
        if (found) {
          this.$store.dispatch('toggleSnack', {
            type: 'error',
            message: 'message.serviceRepeat',
          })
          this.$refs['addServiceDialog'].setLoading(false)
          return
        }
        this.formData.plans.push(obj)
      }

      this.$refs['addServiceDialog'].hide()
    },

    deleteService(id) {
      const found = this.formData.plans.findIndex(el => el.id === id)
      this.formData.plans.splice(found, 1)
    },

    // ----------------- Invoice detail -----------------
    async getBill() {
      try {
        const data = await this.$Fetcher.GetBillById(this.id)
        for (let key in this.formData) {
          if (this.$validate.DataValid(data[key])) {
            this.formData[key] = data[key]
          }
        }
        for (let key in this.staticData) {
          if (this.$validate.DataValid(data[key])) {
            this.staticData[key] = data[key]
          }
        }

        this.billPlans = data.plan_id_list
        if (data.payment_method === 'transfer') {
          if (data.payment_data.indexOf('"') > -1) {
            this.formData.payment_file = JSON.parse(data.payment_data)
          } else {
            this.formData.payment_file = data.payment_data
          }
        }
        await this.getCompany(data.company_id)

        if (data.plan_data_list) {
          this.renderPlanTable(data.plan_data_list)
        } else {
          await this.getPlansByIds()
        }
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setAlert', {
          message: 'noData',
          redirect: 'OrderList',
        })
      } finally {
        this.$store.dispatch('setInitLoading', false)
      }
    },
    async getCompany(id) {
      try {
        const data = await this.$Fetcher.GetCompanyById(id)
        this.selectedCompanyData = data
      } catch {}
    },
    async getPlansByIds() {
      try {
        const idList = this.billPlans.map(el => el.id)
        const data = await this.$Fetcher.GetPlanByIdList(idList)
        this.renderPlanTable(data)
      } catch (err) {
        this.$common.error(err)
      }
    },

    renderPlanTable(plan_data) {
      this.formData.plans = this.billPlans.map(item => {
        const found = plan_data.find(el => el.id === item.id)
        return {
          id: item.id,
          service: found ? this.$Formatter.getServiceName(found.name_zh, this.$t(`planTypeOption.${found.type}`)) : '',
          price: item.price,
          type: found?.type || '',
        }
      })
    },

    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const valid = await this.$refs['paymentStatusForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        return
      }

      this.$store.dispatch('setLoading', true)

      try {
        await this.$Fetcher.UpdateBill(this.id, {
          payment_status: this.formData.payment_status,
          payment_method: this.formData.payment_method,
        })
        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('toggleSnack', {
          type: 'success',
          message: 'message.saveSuccess',
          refresh: true,
        })
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setAlert', {
          title: 'message.saveFail',
          message: err,
          type: 'error',
        })
      }
    },
  },

  async mounted() {
    this.$store.dispatch('setInitLoading', true)
    if (this.id !== null) {
      this.getBill()
    } else {
      await this.searchCompany()
      await this.getAllPlans()
      this.$store.dispatch('setInitLoading', false)
    }
  },
}
</script>
